// FilterPanel.jsx - Filter Controls for User KPI Management

import {
    Autocomplete,
    Box,
    CircularProgress,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userEmailSelector } from 'auth/redux.selectors';
import { usePermission } from 'permissions/hooks';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const FilterPanel = ({
    search,
    userId,
    periodId,
    departmentId,
    reportingManager,
    directLineManager,
    onSearch,
    onFilterChange,
    onReset,
    totalCount
}) => {
    const dispatch = useDispatch();
    const loggedInUserEmail = useSelector(userEmailSelector);
    const hasFullKpiAccess = usePermission('user-kpi.get all user kpis');
    const roleName = useSelector((state) => state.auth?.roleName || state.auth?.user?.roleName || '');
    const isAdmin =
        hasFullKpiAccess ||
        (typeof roleName === 'string' && roleName.toLowerCase().includes('admin'));

    // Get dropdown data from Redux - ensure they're always arrays
    const users = useSelector(selectors.selectAllUsers) || [];
    const periods = useSelector(selectors.selectAllPeriods) || [];
    const departments = useSelector(selectors.selectAllDepartments) || [];
    const reportingManagers = useSelector(selectors.selectAllReportingManagers) || [];
    const apiDirectLineManagers = useSelector(selectors.selectAllDirectLineManagers) || [];
    const loadingDropdowns = useSelector(selectors.selectLoadingDropdowns);

    // Fetch dropdown data on mount or when auth/role changes
    useEffect(() => {
        dispatch(actions.fetchAllDepartmentsRequest());
        dispatch(actions.fetchAllReportingManagersRequest());
        if (loggedInUserEmail) {
            dispatch(actions.fetchDirectLineManagersRequest(isAdmin ? undefined : loggedInUserEmail));
        }
    }, [dispatch, isAdmin, loggedInUserEmail]);

    const availableUsers = useMemo(() => {
        if (!Array.isArray(users)) return [];
        if (!loggedInUserEmail) return users;
        return users.filter(u => (u.user?.email || u.employeeId) !== loggedInUserEmail);
    }, [users, loggedInUserEmail]);

    const directLineManagers = useMemo(() => {
        if (isAdmin) {
            return Array.isArray(reportingManagers) ? reportingManagers : [];
        }
        const me = loggedInUserEmail?.trim().toLowerCase();
        if (!me) return [];

        if (apiDirectLineManagers.length > 0) {
            return apiDirectLineManagers.filter(
                (m) => (m.email || '').trim().toLowerCase() !== me
            );
        }
        if (!Array.isArray(reportingManagers)) return [];
        return reportingManagers
            .filter((rm) => (rm.email || '').trim().toLowerCase() !== me)
            .filter((rm) => {
                const u = users.find(
                    (user) => (user.user?.email || user.employeeId || '').trim().toLowerCase() === (rm.email || '').trim().toLowerCase()
                );
                return u?.reportingManager?.trim().toLowerCase() === me;
            });
    }, [isAdmin, apiDirectLineManagers, reportingManagers, users, loggedInUserEmail]);

    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* User Filter */}
            <Autocomplete
                size="small"
                sx={{ minWidth: 200, '& .MuiInputBase-root': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                options={availableUsers}
                getOptionLabel={(option) => {
                    const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                    const email = option.user?.email || option.employeeId || '';
                    return name ? `${name} (${email})` : email;
                }}
                value={availableUsers.find(u => (u.user?.email || u.employeeId) === userId) || null}
                onChange={(event, newValue) => {
                    onFilterChange('userId', newValue ? (newValue.user?.email || newValue.employeeId) : '');
                }}
                loading={loadingDropdowns}
                renderOption={(props, option) => {
                    const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                    const email = option.user?.email || option.employeeId || '';
                    return (
                        <li {...props}>
                            <Box>
                                <Typography variant="body2">
                                    {name || email}
                                </Typography>
                                {name && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                                        {email}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="User"
                        placeholder="Select user"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Period Filter */}
            <Autocomplete
                size="small"
                sx={{ minWidth: 158, '& .MuiInputBase-root': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                options={Array.isArray(periods) ? periods : []}
                getOptionLabel={(option) => option.name || ''}
                value={Array.isArray(periods) ? periods.find(p => p.id === periodId) || null : null}
                onChange={(event, newValue) => {
                    onFilterChange('periodId', newValue ? newValue.id : '');
                }}
                loading={loadingDropdowns}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Period"
                        placeholder="Select period"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Department Filter */}
            <Autocomplete
                size="small"
                sx={{ minWidth: 158, '& .MuiInputBase-root': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                options={Array.isArray(departments) ? departments : []}
                getOptionLabel={(option) => option.name || ''}
                value={Array.isArray(departments) ? departments.find(d => d.name === departmentId) || null : null}
                onChange={(event, newValue) => {
                    onFilterChange('departmentId', newValue ? newValue.name : '');
                }}
                loading={loadingDropdowns}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Department"
                        placeholder="Select department"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Reporting Manager Filter */}
            <Autocomplete
                size="small"
                sx={{ minWidth: 200, '& .MuiInputBase-root': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                options={Array.isArray(reportingManagers) ? reportingManagers : []}
                getOptionLabel={(option) => {
                    const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                    const email = option.email || '';
                    return name ? `${name} (${email})` : email;
                }}
                value={Array.isArray(reportingManagers) ? reportingManagers.find(rm => rm.email === reportingManager) || null : null}
                onChange={(event, newValue) => {
                    onFilterChange('reportingManager', newValue ? newValue.email : '');
                }}
                loading={loadingDropdowns}
                renderOption={(props, option) => {
                    const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                    const email = option.email || '';
                    return (
                        <li {...props}>
                            <Box>
                                <Typography variant="body2">
                                    {name || email}
                                </Typography>
                                {name && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                                        {email}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Reporting Manager"
                        placeholder="Select manager"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Direct Line Manager Filter */}
            <Autocomplete
                size="small"
                sx={{ minWidth: 200, '& .MuiInputBase-root': { fontSize: '12px' }, '& .MuiInputLabel-root': { fontSize: '12px' } }}
                options={directLineManagers}
                getOptionLabel={(option) => {
                    const name = option.name || [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                    const email = option.email || '';
                    return name ? `${name} (${email})` : email;
                }}
                value={directLineManagers.find(rm => rm.email === directLineManager) || null}
                onChange={(event, newValue) => {
                    onFilterChange('directLineManager', newValue ? newValue.email : '');
                }}
                loading={loadingDropdowns}
                noOptionsText={isAdmin ? "No line managers" : "No direct line managers under you"}
                renderOption={(props, option) => {
                    const name = option.name || [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                    const email = option.email || '';
                    return (
                        <li {...props}>
                            <Box>
                                <Typography variant="body2">
                                    {name || email}
                                </Typography>
                                {name && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                                        {email}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Direct Line Manager"
                        placeholder="Select line manager"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default FilterPanel;
